/**
 * Booking confirmation shared by the home page banner and the chat widget.
 *
 * After a Cal.com booking, the event redirects to `/?booked=<kind>` (set in
 * Cal.com; "forward parameters" stays off so no name or email reaches the
 * URL). Only the exact kinds below ever produce a message, and the
 * parameter's own text is never printed.
 */

export type BookedKind = 'walkthrough' | '15min' | 'consulting';

export const BOOKED_COPY: Record<BookedKind, string> = {
  walkthrough: 'You’re booked for your 15-minute walkthrough. A calendar invite and a confirmation email are on their way.',
  '15min': 'You’re booked for your free 15-minute call. A calendar invite and a confirmation email are on their way.',
  consulting: 'You’re booked for your Operations Consulting call. A calendar invite and a confirmation email with the details are on their way.',
};

export const isBookedKind = (v: unknown): v is BookedKind =>
  v === 'walkthrough' || v === '15min' || v === 'consulting';

/** Which kind a Cal.com event URL is (by its slug). */
export const kindFromUrl = (url: string): BookedKind | null => {
  if (url.includes('/15-minute-walkthrough')) return 'walkthrough';
  if (url.includes('/operations-consulting')) return 'consulting';
  if (/\/15min(\?|$|\/)/.test(url)) return '15min';
  return null;
};

const FLAG = 'bc_booked_shown';
const WINDOW_MS = 60_000;

/** Remember that a confirmation was just shown, so it appears once, not twice. */
export const markBookingShown = (kind: BookedKind) => {
  try { sessionStorage.setItem(FLAG, JSON.stringify({ kind, at: Date.now() })); } catch { /* storage can be blocked */ }
};

export const bookingRecentlyShown = (): boolean => {
  try {
    const raw = sessionStorage.getItem(FLAG);
    if (!raw) return false;
    const { at } = JSON.parse(raw) as { at: number };
    return Date.now() - at < WINDOW_MS;
  } catch { return false; }
};

/** Message the home page posts to its parent when it loads inside the chat's booking iframe. */
export const BOOKED_MESSAGE = 'bc-booked';
